import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api, CATEGORY, fileUrl } from "../lib/api";
import { store, load, clear, K_TEAM } from "../lib/storage";
import { useRoom, useCountdown } from "../lib/useRoom";
import { DigLogo } from "../components/dig/DigLogo";
import { GameCard, CardBack } from "../components/dig/GameCard";
import { ExampleOffer } from "../components/dig/ExampleOffer";
import { LANGS } from "../lib/i18n";
import { TwistOverlay } from "../components/dig/TwistOverlay";
import { playShuffle, playFlip, isMuted, setMuted as sfxSetMuted } from "../lib/sfx";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Loader2, RefreshCw, PenLine, Layers, Lock, Wifi, WifiOff, Camera, Link2, Check, ChevronLeft, ChevronRight, Volume2, VolumeX } from "lucide-react";

const PHASE = {
  lobby: { es: "Sala de espera", en: "Waiting room" },
  reparto: { es: "El Reparto", en: "The Deal" },
  combinacion: { es: "La Combinación", en: "The Combination" },
  skeptic: { es: "El Skeptic", en: "The Skeptic" },
  blueprint: { es: "El Blueprint", en: "The Blueprint" },
  producir: { es: "Producir", en: "Produce" },
  pitch: { es: "Pitch", en: "Pitch" },
  cierre: { es: "Cierre", en: "Closing" },
  closed: { es: "Sesión cerrada", en: "Session closed" },
};

export default function Team() {
  const saved = load(K_TEAM);
  const [teamId, setTeamId] = useState(saved?.teamId || null);
  const [code, setCode] = useState(saved?.code || null);
  const [lang, setLang] = useState("es");
  const [muted, setMutedState] = useState(isMuted());
  const setMuted = (v) => { sfxSetMuted(v); setMutedState(v); };
  const [content, setContent] = useState(null);

  useEffect(() => { api.get("/content").then(({ data }) => setContent(data)).catch(() => {}); }, []);

  const { snap, connected, refetch } = useRoom(teamId ? code : null, { role: "team", teamId });
  const myTeam = snap?.teams?.find((t) => t.id === teamId);
  const session = snap?.session;

  // twist takeover — fires on connect / reconnect when unacked
  const [ackedTwist, setAckedTwist] = useState(0);
  const showTwist = session?.active_twist && (session.twist_version || 0) > Math.max(myTeam?.twist_ack_version || 0, ackedTwist);
  const ackTwist = async () => {
    const v = session.twist_version;
    setAckedTwist(v);
    try { await api.post(`/team/${teamId}/twist-ack`, { version: v }); } catch (e) {}
    refetch();
  };

  const onJoined = (t) => {
    store(K_TEAM, { teamId: t.id, code: t.session_code, deviceToken: t.device_token });
    setTeamId(t.id);
    setCode(t.session_code);
  };

  if (!teamId) return <TeamEntry content={content} onJoined={onJoined} />;

  if (!snap || !myTeam) {
    return <div className="min-h-screen bg-dig-paper flex items-center justify-center">
      <Loader2 className="animate-spin text-dig-blue" size={40} />
    </div>;
  }

  const phase = myTeam.phase || session.phase || "lobby";
  const timer = session.active_timer;

  return (
    <div className="min-h-screen bg-dig-paper text-dig-harbour flex flex-col">
      <TwistOverlay twist={showTwist ? session.active_twist : null} onDismiss={ackTwist} lang={lang} />
      <HUD myTeam={myTeam} phase={phase} lang={lang} setLang={setLang} connected={connected} timer={timer} muted={muted} setMuted={setMuted} />
      <main className="flex-1 flex flex-col">
        {phase === "lobby" && <Lobby myTeam={myTeam} lang={lang} />}
        {phase === "reparto" && <Reparto myTeam={myTeam} code={code} session={session} lang={lang} />}
        {phase === "combinacion" && <Combinacion myTeam={myTeam} lang={lang} />}
        {phase === "skeptic" && <Skeptic myTeam={myTeam} timer={timer} lang={lang} />}
        {phase === "blueprint" && <Blueprint myTeam={myTeam} content={content} lang={lang} />}
        {phase === "producir" && <Producir myTeam={myTeam} code={code} timer={timer} lang={lang} />}
        {phase === "pitch" && <Pitch myTeam={myTeam} timer={timer} content={content} lang={lang} />}
        {phase === "cierre" && <Cierre myTeam={myTeam} code={code} content={content} lang={lang} />}
        {phase === "closed" && <Closed lang={lang} />}
      </main>
    </div>
  );
}

/* ---------------- HUD ---------------- */
const PLAY_PHASES = ["reparto", "combinacion", "skeptic", "blueprint", "producir", "pitch", "cierre"];
function HUD({ myTeam, phase, lang, setLang, connected, timer, muted, setMuted }) {
  const remaining = useCountdown(timer);
  const showTimer = timer?.running && (!timer.team_id || timer.team_id === myTeam.id);
  const pIdx = PLAY_PHASES.indexOf(phase);
  const isYellow = myTeam.color_hex === "#FDBB2F";
  return (
    <header className="sticky top-0 z-30 bg-white border-b border-border px-5 h-14 flex items-center gap-4 flex-none">
      <img src="/dig/DIG-Logo-Horizontal.svg" alt="DIG" style={{ height: 26, transform: "rotate(-2deg)" }} />
      <div className="w-px h-6 bg-border" />
      <div className="rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide" style={{ background: myTeam.color_hex, color: isYellow ? "#12333F" : "#fff" }}>{myTeam.name}</div>
      <div className="text-xs font-bold tracking-[0.16em] text-dig-harbour hidden md:block">{(PHASE[phase]?.[lang] || phase).toUpperCase()}</div>
      <div className="hidden lg:flex items-center gap-1.5 ml-1">
        {PLAY_PHASES.map((_, i) => <span key={i} className="w-2 h-2 rounded-full" style={{ background: pIdx >= 0 && i <= pIdx ? myTeam.color_hex : "var(--dig-harbour-tint)" }} />)}
      </div>
      <div className="flex-1" />
      {showTimer ? (
        <div className="bg-dig-harbour rounded-xl px-3 py-1">
          <span className={`font-mono-dig text-xl font-bold tabular-nums ${remaining <= 10 ? "text-dig-coral dig-pulse" : "text-dig-yellow"}`}>
            {String(Math.floor(remaining / 60)).padStart(2, "0")}:{String(remaining % 60).padStart(2, "0")}
          </span>
        </div>
      ) : null}
      <span className="text-[0.6rem] font-semibold tracking-widest text-dig-harbour/40">{myTeam.session_code}</span>
      {connected ? <Wifi size={18} className="text-dig-turquoise" /> : <WifiOff size={18} className="text-dig-coral" />}
      <button data-testid="team-mute-toggle" onClick={() => setMuted(!muted)} title={muted ? "Unmute" : "Mute"}
        className="text-dig-harbour/60 hover:text-dig-harbour">{muted ? <VolumeX size={18} /> : <Volume2 size={18} />}</button>
      <div className="flex bg-dig-harbour-tint rounded-full p-0.5">
        {LANGS.map((l) => (
          <button key={l.key} data-testid={`team-lang-${l.key}`} onClick={() => setLang(l.key)}
            className={`text-[11px] font-bold rounded-full px-2 py-1 ${lang === l.key ? "bg-dig-harbour text-white" : "text-dig-harbour/60"}`}>{l.label}</button>
        ))}
      </div>
    </header>
  );
}

/* ---------------- Entry / Setup / Recovery ---------------- */
function TeamEntry({ content, onJoined }) {
  const [mode, setMode] = useState("new");
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [color, setColor] = useState("blue");
  const [years, setYears] = useState(5);
  const [busy, setBusy] = useState(false);
  const [recoverTeams, setRecoverTeams] = useState(null);
  const [err, setErr] = useState("");
  const colors = content?.team_colors || [];

  const createTeam = async () => {
    setErr("");
    if (!code.trim()) { setErr("Escribe el código de la sala."); return; }
    if (!name.trim()) { setErr("Escribe el nombre del equipo."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/session/${code.trim().toUpperCase()}/team`, { name: name.trim(), color, years_in_role: Number(years) });
      onJoined(data);
    } catch (e) {
      const msg = e?.response?.status === 404
        ? "Sala no encontrada. Pide el código al facilitador."
        : "No se pudo crear el equipo. Revisa tu conexión e inténtalo de nuevo.";
      setErr(msg);
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const lookup = async () => {
    setErr("");
    if (!code.trim()) { setErr("Escribe el código de la sala."); return; }
    setBusy(true);
    try {
      const { data } = await api.get(`/session/${code.trim().toUpperCase()}`);
      setRecoverTeams(data.teams || []);
    } catch (e) {
      const msg = e?.response?.status === 404 ? "Sala no encontrada. Pide el código al facilitador." : "No se pudo buscar la sala.";
      setErr(msg);
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const recover = async (t) => {
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.post(`/session/${code.trim().toUpperCase()}/team/${t.id}/recover`);
      onJoined(data);
      toast.success(`Reanudado: ${data.name}`);
    } catch (e) { setErr("No se pudo reanudar el equipo."); toast.error("No se pudo reanudar."); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-dig-blue flex items-center justify-center px-6 py-10">
      <div className="w-full max-w-lg bg-white rounded-3xl p-8 dig-card-shadow text-dig-harbour">
        <div className="flex justify-center mb-6"><DigLogo /></div>
        <div className="flex gap-2 mb-6 bg-dig-harbour-tint rounded-full p-1">
          <button data-testid="team-mode-new" onClick={() => setMode("new")}
            className={`flex-1 rounded-full py-2 text-sm font-bold transition-colors ${mode === "new" ? "bg-white shadow" : "text-dig-harbour/50"}`}>Nuevo equipo</button>
          <button data-testid="team-mode-recover" onClick={() => { setMode("recover"); setRecoverTeams(null); }}
            className={`flex-1 rounded-full py-2 text-sm font-bold transition-colors ${mode === "recover" ? "bg-white shadow" : "text-dig-harbour/50"}`}>Recuperar</button>
        </div>

        <label className="text-xs font-semibold tracking-wide text-dig-harbour/60">CÓDIGO DE SALA</label>
        <Input data-testid="team-code-input" value={code} onChange={(e) => { setCode(e.target.value.toUpperCase()); setErr(""); }}
          placeholder="ABC12" maxLength={6} className="mt-1 mb-5 text-center text-2xl font-mono-dig tracking-[0.3em] h-14" />

        {err ? (
          <div data-testid="team-entry-error" className="mb-4 rounded-xl bg-dig-coral-tint text-dig-coral text-sm font-semibold px-4 py-3">
            {err}
          </div>
        ) : null}

        {mode === "new" ? (
          <>
            <label className="text-xs font-semibold tracking-wide text-dig-harbour/60">NOMBRE DEL EQUIPO</label>
            <Input data-testid="team-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Los Flamencos" className="mt-1 mb-5 h-12" />
            <label className="text-xs font-semibold tracking-wide text-dig-harbour/60">COLOR</label>
            <div className="flex gap-3 mt-2 mb-5">
              {colors.map((c) => (
                <button key={c.key} data-testid={`team-color-${c.key}`} onClick={() => setColor(c.key)}
                  className={`w-11 h-11 rounded-xl transition-transform ${color === c.key ? "ring-4 ring-offset-2 ring-dig-harbour scale-110" : ""}`}
                  style={{ background: c.hex }} title={c.name_es} />
              ))}
            </div>
            <label className="text-xs font-semibold tracking-wide text-dig-harbour/60">AÑOS EN EL ROL (del más veterano)</label>
            <Input data-testid="team-years-input" type="number" min={0} value={years} onChange={(e) => setYears(e.target.value)} className="mt-1 mb-6 h-12" />
            <Button data-testid="team-create-btn" onClick={createTeam} disabled={busy}
              className="w-full h-12 rounded-full bg-dig-blue hover:bg-dig-blue/90 text-white font-bold">
              {busy ? <Loader2 className="animate-spin" /> : "Crear equipo"}
            </Button>
          </>
        ) : (
          <>
            {recoverTeams === null ? (
              <Button data-testid="team-lookup-btn" onClick={lookup} disabled={busy} className="w-full h-12 rounded-full bg-dig-blue text-white font-bold">
                {busy ? <Loader2 className="animate-spin" /> : "Buscar equipos"}
              </Button>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-dig-harbour/60 mb-2">Elige tu equipo para reanudar en el punto exacto:</p>
                {recoverTeams.length === 0 && <p className="text-sm text-dig-coral">No hay equipos en esta sala todavía.</p>}
                {recoverTeams.map((t) => (
                  <button key={t.id} data-testid={`recover-team-${t.id}`} onClick={() => recover(t)}
                    className="w-full flex items-center gap-3 p-3 rounded-xl border border-border hover:bg-dig-paper text-left">
                    <div className="w-6 h-6 rounded-md" style={{ background: t.color_hex }} />
                    <span className="font-semibold flex-1">{t.name}</span>
                    <span className="text-xs text-dig-harbour/50">{PHASE[t.phase]?.es || t.phase}</span>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* ---------------- Lobby ---------------- */
function Lobby({ myTeam, lang }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center px-8 py-16">
      <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="w-24 h-24 rounded-3xl mb-8 flex items-center justify-center" style={{ background: myTeam.color_hex }}>
        <Layers size={44} className="text-white" strokeWidth={2.5} />
      </motion.div>
      <h1 className="text-4xl font-black tracking-tight mb-3">{myTeam.name}</h1>
      <p className="text-lg text-dig-harbour/60 max-w-md">
        {lang === "es" ? "Listos. Esperando a que el facilitador empiece la ronda." : "Ready. Waiting for the facilitator to start the round."}
      </p>
      <div className="mt-6 flex items-center gap-2 text-dig-turquoise font-semibold">
        <span className="w-2.5 h-2.5 rounded-full bg-dig-turquoise dig-pulse" /> {lang === "es" ? "Conectado" : "Connected"}
      </div>
    </div>
  );
}

/* ---------------- El Reparto ---------------- */
function HandFlipCard({ card, revealed, onReveal, lang, w = 220, tilt = 0, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.6, y: -40 }}
      animate={{ opacity: 1, scale: 1, y: 0, rotate: tilt }}
      transition={{ delay, type: "spring", stiffness: 220, damping: 20 }}
      style={{ perspective: 1200, width: w, cursor: revealed ? "default" : "pointer" }}
      onClick={() => !revealed && onReveal()}
      data-testid={`team-flip-card-${card.slot}`}
    >
      <motion.div
        animate={{ rotateY: revealed ? 180 : 0, y: revealed ? 0 : [0, -7, 0] }}
        transition={{ rotateY: { duration: 0.55, ease: [0.2, 0.8, 0.3, 1] }, y: { duration: 1.8, repeat: revealed ? 0 : Infinity, ease: "easeInOut" } }}
        style={{ position: "relative", transformStyle: "preserve-3d", width: w, aspectRatio: "63 / 88", borderRadius: w * 0.067, background: "#fff" }}
      >
        <div style={{ position: "absolute", inset: 0, backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden" }}>
          <CardBack width={w} />
        </div>
        <div style={{ position: "absolute", inset: 0, backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden", transform: "rotateY(180deg)" }}>
          <GameCard category={card.slot} code={card.card_code} es={card.es} en={card.en} isBlank={card.is_blank} blankText={card.blank_text} width={w} lang={lang} />
        </div>
      </motion.div>
    </motion.div>
  );
}

function Reparto({ myTeam, code, session, lang }) {
  const [busy, setBusy] = useState(false);
  const [blankSlot, setBlankSlot] = useState(null);
  const [blankText, setBlankText] = useState("");
  const [dealing, setDealing] = useState(false);
  const [revealed, setRevealed] = useState([]);
  const [tools, setTools] = useState([]);
  const hand = myTeam.hand || [];
  const dealtCards = hand.filter((c) => c.slot !== "TOOL");
  const toolCard = hand.find((c) => c.slot === "TOOL");
  const redrawLimit = session?.settings?.redraw_limit ?? 1;
  const redrawsLeft = Math.max(0, redrawLimit - (myTeam.redraws_used || 0));
  const locked = myTeam.hand_locked;
  const maxChars = session?.settings?.blank_max_chars ?? 120;
  const isRevealed = (slot) => locked || revealed.includes(slot);
  const reveal = (slot) => setRevealed((r) => (r.includes(slot) ? r : (playFlip(), [...r, slot])));
  const coreSlotsEnabled = ["PARTNER", "PAIN", "TRIBE"].some((s) => session?.settings?.[`include_${s.toLowerCase()}`] !== false);
  const allRevealed = dealtCards.every((c) => isRevealed(c.slot));
  const canLock = allRevealed && !!toolCard;

  useEffect(() => {
    api.get(`/session/${code}/cards`).then(({ data }) => setTools(data.grouped?.TOOL || [])).catch(() => {});
  }, [code]);

  const deal = async () => {
    setDealing(true);
    playShuffle();
    setTimeout(async () => {
      try { await api.post(`/team/${myTeam.id}/deal`); playFlip(); } finally { setDealing(false); }
    }, 900);
  };
  const chooseTool = async (card) => {
    setBusy(true);
    try { await api.post(`/team/${myTeam.id}/choose-tool`, { card_code: card.code }); playFlip(); }
    catch (e) { toast.error(lang === "es" ? "No se pudo elegir" : "Could not choose"); }
    finally { setBusy(false); }
  };
  const redraw = async (slot) => { setBusy(true); try { await api.post(`/team/${myTeam.id}/redraw`, { slot }); playFlip(); } catch (e) { toast.error("Sin redraws"); } finally { setBusy(false); } };
  const saveBlank = async () => {
    setBusy(true);
    try { await api.post(`/team/${myTeam.id}/blank`, { slot: blankSlot, text: blankText }); setBlankSlot(null); setBlankText(""); }
    finally { setBusy(false); }
  };
  const lock = async () => { setBusy(true); try { await api.post(`/team/${myTeam.id}/lock`); toast.success(lang === "es" ? "Mano bloqueada" : "Hand locked"); } finally { setBusy(false); } };

  if (hand.length === 0 && coreSlotsEnabled) {
    const decks = ["PARTNER", "PAIN", "TRIBE"].filter((s) => session?.settings?.[`include_${s.toLowerCase()}`] !== false);
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-center px-8">
        <h1 className="text-3xl font-black mb-2">{lang === "es" ? "El Reparto" : "The Deal"}</h1>
        <p className="text-dig-harbour/60 mb-8 max-w-md">{lang === "en" ? "Tap to shuffle and deal your cards. Then choose your Tool from the full deck." : "Toca para barajar y repartir tus cartas. Luego elige tu Herramienta de la baraja completa."}</p>
        <div className="max-w-2xl w-full mb-8"><ExampleOffer lang={lang} compact /></div>
        <div className="flex items-end gap-6 mb-10">
          {decks.map((d, i) => (
            <div key={d} className="relative" style={{ width: 122, height: 171 }} data-testid={`deck-${d}`}>
              <div className="absolute inset-0 rounded-[9px]" style={{ background: CATEGORY[d].hex, transform: "rotate(-4deg)", filter: "brightness(0.8)" }} />
              <div className="absolute inset-0 rounded-[9px]" style={{ background: CATEGORY[d].hex, transform: "rotate(2.5deg)", filter: "brightness(0.9)" }} />
              <motion.div className="absolute inset-0"
                animate={dealing ? { x: [0, (i % 2 ? 1 : -1) * 24, 0], y: [0, -14, 0], rotate: [0, (i % 2 ? 8 : -8), 0] } : { rotate: 0 }}
                transition={dealing ? { duration: 0.8, ease: "easeInOut" } : {}}>
                <CardBack width={122} />
              </motion.div>
            </div>
          ))}
        </div>
        <Button data-testid="deal-cards-btn" onClick={deal} disabled={dealing}
          className="h-14 px-10 rounded-full bg-dig-coral hover:bg-dig-coral/90 text-white font-bold text-lg">
          {dealing ? (lang === "es" ? "Barajando…" : "Shuffling…") : (lang === "es" ? "Repartir cartas" : "Deal cards")}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col px-8 py-6 overflow-y-auto dig-scroll">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-black">{lang === "es" ? "Tu mano" : "Your hand"}</h1>
          <p className="text-sm text-dig-harbour/50">
            {locked ? (lang === "es" ? "Mano bloqueada" : "Hand locked")
              : !allRevealed ? (lang === "es" ? "Toca cada carta para revelarla" : "Tap each card to reveal it")
              : !toolCard ? (lang === "es" ? "Ahora elige tu Herramienta abajo" : "Now choose your Tool below")
              : (lang === "es" ? `${redrawsLeft} redraw disponible(s)` : `${redrawsLeft} redraw(s) left`)}
          </p>
        </div>
        <div className="flex gap-3">
          {!locked ? (
            <Button data-testid="lock-hand-btn" onClick={lock} disabled={busy || !canLock} className="rounded-full bg-dig-harbour text-white font-bold">
              <Lock size={18} className="mr-2" />{lang === "es" ? "Bloquear mano" : "Lock hand"}
            </Button>
          ) : (
            <span className="flex items-center gap-2 text-dig-turquoise font-bold"><Lock size={18} />{lang === "es" ? "Bloqueada" : "Locked"}</span>
          )}
        </div>
      </div>

      <div className="flex items-center justify-center gap-6 flex-wrap">
        {dealtCards.map((c, i) => (
          <div key={c.slot} className="flex flex-col items-center gap-3">
            <HandFlipCard card={c} lang={lang} w={200} tilt={(i - 1) * 2} delay={i * 0.1}
              revealed={isRevealed(c.slot)} onReveal={() => reveal(c.slot)} />
            {!locked && isRevealed(c.slot) ? (
              <div className="flex gap-2">
                <Button data-testid={`redraw-${c.slot}`} onClick={() => redraw(c.slot)} disabled={busy || redrawsLeft <= 0} size="sm"
                  variant="outline" className="rounded-full text-xs"><RefreshCw size={14} className="mr-1" />{lang === "es" ? "Cambiar" : "Redraw"}</Button>
                <Button data-testid={`blank-${c.slot}`} onClick={() => { setBlankSlot(c.slot); setBlankText(c.is_blank ? c.blank_text : ""); }} size="sm"
                  variant="outline" className="rounded-full text-xs"><PenLine size={14} className="mr-1" />{lang === "es" ? "Escribir" : "Write"}</Button>
              </div>
            ) : null}
          </div>
        ))}
        {toolCard ? (
          <div className="flex flex-col items-center gap-3">
            <div style={{ transform: "rotate(2deg)" }}>
              <GameCard category="TOOL" code={toolCard.card_code} es={toolCard.es} en={toolCard.en} width={200} lang={lang} />
            </div>
            <span className="text-xs font-bold text-dig-turquoise flex items-center gap-1"><Check size={14} />{lang === "es" ? "Tu Herramienta" : "Your Tool"}</span>
          </div>
        ) : null}
      </div>

      {!locked && !allRevealed ? (
        <button data-testid="reveal-all-btn" onClick={() => { playFlip(); setRevealed(dealtCards.map((c) => c.slot)); }}
          className="mx-auto mt-4 text-sm text-dig-harbour/50 underline">{lang === "es" ? "Revelar todas" : "Reveal all"}</button>
      ) : null}

      {/* Tool gallery — browse the full Tool deck and pick one */}
      {!locked && allRevealed ? (
        <div className="mt-8 border-t border-border pt-6" data-testid="tool-gallery">
          <div className="flex items-center gap-2 mb-1">
            <span className="w-3 h-3 rounded-full" style={{ background: CATEGORY.TOOL.hex }} />
            <h2 className="text-xl font-black">{lang === "es" ? "Elige tu Herramienta" : "Choose your Tool"}</h2>
            <span className="text-sm text-dig-harbour/40">{tools.length}</span>
          </div>
          <p className="text-sm text-dig-harbour/50 mb-4">{lang === "es" ? "Desliza y toca una Herramienta para diseñar tu oferta." : "Scroll and tap a Tool to design your offering."}</p>
          <div className="flex gap-4 overflow-x-auto pb-4 dig-scroll">
            {tools.map((t) => {
              const active = toolCard?.card_code === t.code;
              return (
                <button key={t.id} data-testid={`tool-pick-${t.code}`} onClick={() => chooseTool(t)} disabled={busy}
                  className={`flex-none rounded-2xl transition-transform ${active ? "ring-4 ring-dig-turquoise scale-[1.03]" : "hover:-translate-y-1"}`}>
                  <GameCard category="TOOL" code={t.code} es={t.es} en={t.en} width={150} lang={lang} />
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      <Dialog open={!!blankSlot} onOpenChange={(o) => !o && setBlankSlot(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>{lang === "es" ? "Escribe tu carta" : "Write your card"} · {blankSlot}</DialogTitle></DialogHeader>
          <Textarea data-testid="blank-text-input" value={blankText} maxLength={maxChars}
            onChange={(e) => setBlankText(e.target.value)} placeholder={lang === "es" ? "Máx. 120 caracteres" : "Max 120 chars"} className="min-h-[100px]" />
          <p className="text-xs text-dig-harbour/50 text-right">{blankText.length}/{maxChars}</p>
          <DialogFooter>
            <Button data-testid="blank-save-btn" onClick={saveBlank} disabled={busy || !blankText.trim()} className="rounded-full bg-dig-coral text-white font-bold">
              {lang === "es" ? "Guardar" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- La Combinación ---------------- */
function Combinacion({ myTeam, lang }) {
  const [name, setName] = useState(myTeam.offer_name || "");
  const [sentence, setSentence] = useState(myTeam.offer_sentence || "");
  const t = useRef(null);
  const save = useCallback((patch) => {
    if (t.current) clearTimeout(t.current);
    t.current = setTimeout(() => { api.post(`/team/${myTeam.id}/offer`, patch).catch(() => {}); }, 400);
  }, [myTeam.id]);

  return (
    <div className="flex-1 px-8 py-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
      <div className="lg:col-span-7">
        <h1 className="text-3xl font-black mb-1">{lang === "es" ? "La Combinación" : "The Combination"}</h1>
        <p className="text-dig-harbour/60 mb-8">{lang === "es" ? "Nombra tu oferta y descríbela en una frase. Se guarda solo." : "Name your offer and describe it in one sentence. It autosaves."}</p>
        <label className="text-xs font-semibold tracking-widest text-dig-blue">NOMBRE DE LA OFERTA</label>
        <Input data-testid="offer-name-input" value={name} onChange={(e) => { setName(e.target.value); save({ offer_name: e.target.value }); }}
          placeholder={lang === "es" ? "La Isla que No Viste" : "The Island You Never Saw"} className="mt-1 mb-6 h-14 text-xl font-bold" />
        <label className="text-xs font-semibold tracking-widest text-dig-blue">EN UNA FRASE</label>
        <Textarea data-testid="offer-sentence-input" value={sentence} onChange={(e) => { setSentence(e.target.value); save({ offer_sentence: e.target.value }); }}
          placeholder={lang === "es" ? "Ayudamos a [socio] a resolver [dolor] para [tribu] con [herramienta]." : "We help [partner] solve [pain] for [tribe] with [tool]."}
          className="mt-1 min-h-[140px] text-lg" />
        <p className="mt-3 text-sm text-dig-turquoise font-semibold flex items-center gap-1"><Check size={16} /> {lang === "es" ? "Guardado automático" : "Autosaved"}</p>
      </div>
      <div className="lg:col-span-5">
        <p className="text-xs font-semibold tracking-widest text-dig-harbour/40 mb-3">TU MANO</p>
        <div className="grid grid-cols-2 gap-3">
          {(myTeam.hand || []).map((c) => (
            <GameCard key={c.slot} category={c.slot} code={c.card_code} es={c.es} en={c.en} isBlank={c.is_blank} blankText={c.blank_text} size="sm" lang={lang} />
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------------- El Skeptic ---------------- */
function Skeptic({ myTeam, timer, lang }) {
  const remaining = useCountdown(timer);
  const active = timer?.running && timer?.label === "skeptic";
  const total = timer?.duration || 900;
  const pct = active ? Math.max(0, Math.min(100, (remaining / total) * 100)) : 100;
  const low = remaining <= 30 && remaining > 0;
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center px-8 bg-dig-harbour text-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "url(/dig/icons/icon-pain.png)", backgroundSize: "220px", backgroundRepeat: "repeat" }} />
      <div className="relative flex flex-col items-center">
        <div className="flex items-center gap-3 mb-5">
          <span className="text-dig-coral font-bold tracking-[0.3em]">EL SKEPTIC · 15 MIN</span>
        </div>
        <h1 className="text-4xl md:text-5xl font-black max-w-3xl leading-tight mb-5">
          {lang === "es" ? "El más veterano del equipo es el Skeptic." : "The most experienced person is the Skeptic."}
        </h1>
        <p className="text-white/60 max-w-xl text-lg mb-10">
          {lang === "es"
            ? "Durante 15 minutos, su único trabajo es argumentar por qué la idea va a fracasar. Después, ayuda a arreglarla."
            : "For 15 minutes, their only job is to argue why the idea will fail. After that, they help fix it."}
        </p>
        {active ? (
          <div className="w-full max-w-xl">
            <div className={`font-mono-dig font-bold ${low ? "text-dig-coral dig-pulse" : "text-dig-yellow"}`} style={{ fontSize: "clamp(3.5rem,12vw,8rem)", lineHeight: 1 }}>
              {String(Math.floor(remaining / 60)).padStart(2, "0")}:{String(remaining % 60).padStart(2, "0")}
            </div>
            <div className="mt-6 h-2.5 rounded-full bg-white/15 overflow-hidden">
              <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${pct}%`, background: low ? "#F2695C" : "#FDBB2F" }} />
            </div>
          </div>
        ) : (
          <div className="mt-2 rounded-full border border-white/20 px-6 py-3 text-white/50">{lang === "es" ? "Esperando al facilitador para iniciar el reloj…" : "Waiting for the facilitator to start the clock…"}</div>
        )}
      </div>
    </div>
  );
}

/* ---------------- El Blueprint ---------------- */
function Blueprint({ myTeam, content, lang }) {
  const boxes = content?.blueprint || [];
  const idx = Math.min(myTeam.blueprint_box || 0, boxes.length - 1);
  const box = boxes[idx];
  const go = async (i) => { const n = Math.max(0, Math.min(boxes.length - 1, i)); await api.post(`/team/${myTeam.id}/blueprint`, { index: n }).catch(() => {}); };
  if (!box) return null;
  const isKey = idx === 5;
  return (
    <div className="flex-1 px-8 py-5 flex flex-col min-h-0">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[0.65rem] font-bold tracking-[0.16em] text-dig-harbour/40">EL BLUEPRINT · CANVAS</div>
          <h1 className="text-2xl font-black">{myTeam.offer_name || (lang === "es" ? "Tu lienzo" : "Your canvas")}</h1>
        </div>
        <span className="font-mono-dig text-sm text-dig-harbour/50">{idx + 1} / {boxes.length}</span>
      </div>

      {/* labeled canvas grid */}
      <div className="grid grid-cols-4 gap-2 mb-4">
        {boxes.map((b, i) => {
          const active = i === idx, done = i < idx, key6 = i === 5;
          return (
            <button key={i} data-testid={`blueprint-box-${i}`} onClick={() => go(i)}
              className="h-16 rounded-xl border-2 px-2.5 py-1.5 flex flex-col justify-between text-left transition-all"
              style={{
                background: active ? "#12333F" : key6 ? "#FEF2D8" : "#fff",
                borderColor: active ? "#12333F" : done ? myTeam.color_hex : "rgba(18,51,63,.18)",
                borderStyle: done || active || key6 ? "solid" : "dashed",
                transform: active ? "scale(1.03)" : "none",
              }}>
              <span className="text-[10px] font-bold" style={{ color: active ? "#FDBB2F" : "#1B7FA8" }}>{key6 ? "★" : ""} {i + 1}</span>
              <span className="text-[10px] leading-tight font-semibold line-clamp-2" style={{ color: active ? "#fff" : "#12333F" }}>{lang === "es" ? b.es : b.en}</span>
            </button>
          );
        })}
      </div>

      <motion.div key={idx} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }}
        className={`flex-1 min-h-0 rounded-2xl p-8 flex flex-col justify-center ${isKey ? "" : "bg-white border border-border"} dig-card-shadow`}
        style={isKey ? { background: "#FDBB2F" } : {}}>
        {isKey ? <span className="font-bold tracking-widest text-dig-harbour/70 mb-2 text-sm">★ {lang === "es" ? "LA CAJA CLAVE" : "THE KEY BOX"}</span> : <span className="text-xs font-bold tracking-widest text-dig-blue mb-2">{lang === "es" ? `CAJA ${idx + 1}` : `BOX ${idx + 1}`}</span>}
        <h2 className="text-3xl md:text-4xl font-black leading-tight">{lang === "es" ? box.es : box.en}</h2>
        <p className="mt-2 text-lg text-dig-harbour/60">{lang === "es" ? box.en : box.es}</p>
        <div className="mt-5 pt-4 border-t border-dig-harbour/10">
          <p className="text-base font-medium italic text-dig-harbour/80">{lang === "es" ? box.coach_es : box.coach_en}</p>
        </div>
      </motion.div>

      <div className="flex items-center justify-between mt-4 gap-4">
        <Button data-testid="blueprint-prev" onClick={() => go(idx - 1)} disabled={idx === 0} variant="outline" className="rounded-full font-bold"><ChevronLeft size={18} className="mr-1" />{lang === "es" ? "Anterior" : "Prev"}</Button>
        {/* hand rail */}
        <div className="flex-1 flex items-center justify-center gap-2 overflow-hidden">
          {(myTeam.hand || []).map((c) => <GameCard key={c.slot} category={c.slot} code={c.card_code} es={c.es} en={c.en} isBlank={c.is_blank} blankText={c.blank_text} width={62} lang={lang} />)}
        </div>
        <Button data-testid="blueprint-next" onClick={() => go(idx + 1)} disabled={idx === boxes.length - 1} className="rounded-full bg-dig-blue text-white font-bold">{lang === "es" ? "Siguiente" : "Next"}<ChevronRight size={18} className="ml-1" /></Button>
      </div>
    </div>
  );
}

/* ---------------- Producir ---------------- */
function Producir({ myTeam, code, timer, lang }) {
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState(myTeam.artifact?.link || "");
  const fileRef = useRef();
  const artifact = myTeam.artifact;

  const uploadPhoto = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post(`/session/${code}/upload?kind=artifact`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/team/${myTeam.id}/artifact`, { type: "photo", url: data.url });
      toast.success(lang === "es" ? "Foto subida" : "Photo uploaded");
    } catch (e) { toast.error(lang === "es" ? "Error al subir" : "Upload failed"); }
    finally { setBusy(false); }
  };
  const saveLink = async () => {
    if (!link.trim()) return;
    setBusy(true);
    try { await api.post(`/team/${myTeam.id}/artifact`, { type: "link", link: link.trim() }); toast.success(lang === "es" ? "Link guardado" : "Link saved"); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex-1 px-8 py-8 max-w-3xl">
      <div className="text-[0.65rem] font-bold tracking-[0.16em] text-dig-harbour/40 mb-1">PRODUCIR · ARTEFACTO</div>
      <h1 className="text-3xl font-black mb-1">{lang === "es" ? "Construyan algo real" : "Build something real"}</h1>
      <p className="text-dig-harbour/60 mb-8">{lang === "es" ? "Hagan un artefacto con IA. Suban una foto o peguen un link." : "Make an artifact with AI. Upload a photo or paste a link."}</p>

      {artifact?.url ? (
        <div className="mb-6 relative w-fit">
          <img src={fileUrl(artifact.url)} alt="artifact" className="max-h-64 rounded-2xl dig-card-shadow" />
          <span className="absolute top-2 right-2 bg-dig-turquoise text-dig-harbour rounded-full px-2.5 py-1 text-xs font-bold flex items-center gap-1"><Check size={13} />{lang === "es" ? "Guardado" : "Saved"}</span>
        </div>
      ) : null}
      {artifact?.link ? <p className="mb-6 text-dig-blue break-all"><Link2 size={16} className="inline mr-1" />{artifact.link}</p> : null}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <button data-testid="artifact-photo-btn" onClick={() => fileRef.current?.click()} disabled={busy}
          className="bg-white rounded-2xl p-8 border border-border dig-card-shadow flex flex-col items-center gap-3 hover:dig-card-lift transition-shadow" style={{ borderTop: "6px solid #F2695C" }}>
          {busy ? <Loader2 className="animate-spin text-dig-coral" size={32} /> : <Camera size={32} className="text-dig-coral" />}
          <span className="font-bold">{lang === "es" ? "Subir foto" : "Upload photo"}</span>
        </button>
        <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={uploadPhoto} className="hidden" />
        <div className="bg-white rounded-2xl p-6 border border-border dig-card-shadow" style={{ borderTop: "6px solid #1B7FA8" }}>
          <label className="text-xs font-semibold tracking-widest text-dig-blue">LINK</label>
          <Input data-testid="artifact-link-input" value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://…" className="mt-1 mb-3" />
          <Button data-testid="artifact-link-save" onClick={saveLink} disabled={busy} className="w-full rounded-full bg-dig-blue text-white font-bold">{lang === "es" ? "Guardar link" : "Save link"}</Button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Pitch ---------------- */
function Pitch({ myTeam, timer, content, lang }) {
  const remaining = useCountdown(timer);
  const mine = timer?.running && timer?.label === "pitch" && timer?.team_id === myTeam.id;
  const total = timer?.duration || 90;
  const pct = mine ? Math.max(0, Math.min(100, (remaining / total) * 100)) : 100;
  const low = remaining <= 15 && remaining > 0;
  const questions = content?.buyer_questions || [];
  if (mine) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-center px-8 bg-dig-coral text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "url(/dig/icons/icon-pitch.png)", backgroundSize: "200px", backgroundRepeat: "repeat" }} />
        <div className="relative flex flex-col items-center w-full max-w-2xl">
          <span className="font-bold tracking-[0.35em] mb-3 dig-pulse">¡TU PITCH!</span>
          <div className={`font-mono-dig font-bold ${low ? "dig-pulse" : ""}`} style={{ fontSize: "clamp(5rem,22vw,14rem)", lineHeight: 0.9, color: low ? "#12333F" : "#fff" }}>
            {String(Math.floor(remaining / 60)).padStart(2, "0")}:{String(remaining % 60).padStart(2, "0")}
          </div>
          <div className="mt-6 w-full h-3 rounded-full bg-white/25 overflow-hidden">
            <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${pct}%`, background: "#12333F" }} />
          </div>
          {myTeam.offer_name ? <p className="mt-5 text-xl font-black">{myTeam.offer_name}</p> : null}
        </div>
      </div>
    );
  }
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center px-8">
      <div className="text-[0.65rem] font-bold tracking-[0.16em] text-dig-harbour/40 mb-2">PITCH · 90 SEGUNDOS</div>
      <h1 className="text-3xl font-black mb-2">{lang === "es" ? "Prepárense para vender" : "Get ready to pitch"}</h1>
      <p className="text-dig-harbour/60 max-w-md mb-8">{lang === "es" ? "El facilitador dispara el reloj de 90 segundos cuando sea su turno." : "The facilitator triggers the 90-second clock when it's your turn."}</p>
      <div className="bg-white rounded-2xl p-6 border border-border dig-card-shadow max-w-lg text-left w-full">
        <p className="text-xs font-bold tracking-widest text-dig-coral mb-3">{lang === "es" ? "LOS COMPRADORES PREGUNTARÁN" : "THE BUYERS WILL ASK"}</p>
        <ul className="space-y-2">
          {questions.map((q, i) => (
            <li key={i} className="flex gap-2.5 items-start text-sm">
              <span className="w-5 h-5 rounded-full bg-dig-coral text-white text-[11px] font-bold flex items-center justify-center flex-none mt-0.5">{i + 1}</span>
              <span>{lang === "es" ? q.es : q.en}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/* ---------------- Cierre ---------------- */
function Cierre({ myTeam, code, content, lang }) {
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();
  const photos = myTeam.commitment_photos || [];
  const tpl = content?.commitment_template;

  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post(`/session/${code}/upload?kind=commitment`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/team/${myTeam.id}/commitment`, { url: data.url });
      toast.success(lang === "es" ? "Compromiso capturado" : "Commitment captured");
    } catch (e) { toast.error(lang === "es" ? "Error al subir" : "Upload failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex-1 px-8 py-8 max-w-3xl">
      <div className="text-[0.65rem] font-bold tracking-[0.16em] text-dig-harbour/40 mb-1">CIERRE · COMPROMISO</div>
      <h1 className="text-3xl font-black mb-1">{lang === "es" ? "El compromiso" : "The commitment"}</h1>
      <p className="text-dig-harbour/60 mb-6">{lang === "es" ? "Capturen las tarjetas de compromiso del equipo." : "Capture the team's commitment cards."}</p>
      {tpl ? <div className="bg-dig-yellow-tint border border-dig-yellow/40 rounded-2xl p-5 mb-6"><p className="font-medium text-lg leading-relaxed">{lang === "es" ? tpl.es : tpl.en}</p></div> : null}
      <Button data-testid="commitment-photo-btn" onClick={() => fileRef.current?.click()} disabled={busy}
        className="rounded-full bg-dig-blue text-white font-bold h-12 px-8">
        {busy ? <Loader2 className="animate-spin" /> : <><Camera size={18} className="mr-2" />{lang === "es" ? "Tomar foto" : "Take photo"}</>}
      </Button>
      <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={upload} className="hidden" />
      {photos.length ? <p className="text-sm text-dig-turquoise font-semibold mt-4 flex items-center gap-1"><Check size={15} />{photos.length} {lang === "es" ? "capturada(s)" : "captured"}</p> : null}
      <div className="grid grid-cols-3 gap-3 mt-4">
        {photos.map((u, i) => <img key={i} src={fileUrl(u)} alt="commitment" className="rounded-xl dig-card-shadow aspect-square object-cover" />)}
      </div>
    </div>
  );
}

function Closed({ lang }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center px-8">
      <DigLogo size={64} showText={false} />
      <h1 className="text-4xl font-black mt-8 mb-3">{lang === "es" ? "¡Gracias por jugar!" : "Thanks for playing!"}</h1>
      <p className="text-dig-harbour/60 max-w-md">{lang === "es" ? "Dejaste de vender el destino. Empezaste a resolver el problema del socio." : "You stopped selling the destination. You started solving the partner's problem."}</p>
    </div>
  );
}
